import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed } from '@angular/core';
import { INIT_MODES, IReportTabState, InitMode } from '@navstation-portal/reporting/data-model/vessel-report';
import { PositionAndWeatherState, PropellerPitchState } from '@navstation-portal/reporting/data-model/vessel-report';
import { LatLngModel, SelectOption } from '@navstation-portal/shared/data-model/common';
import { tvaTimezoneToSelectOption } from '@navstation-portal/shared/util/time';
import { PropellerTypes, VesselSpec } from '@navstation-portal/vessels/data-model/vessel';
import { signalStore, withComputed, withState } from '@ngrx/signals';
import { SWELL_OPTIONS } from '../../constants/swell.constants';
import { initStateField } from '../../utils/store.utility';
import { withCalculatedValues } from '../with-calculated-values';
import { withFormStoreReducer } from '../with-form-store-reducer';
import { positionAndWeatherEvents } from './position-and-weather-events';
import { buildPositionAndWeatherVm } from './position-and-weather-vm-builder';
import { withPositionAndWeatherEffects } from './with-position-and-weather-effects';

const createEmptyLatLng = () => ({
  latDegrees: undefined,
  latMinutes: undefined,
  latSeconds: undefined,
  latDirection: undefined,
  longDegrees: undefined,
  longMinutes: undefined,
  longSeconds: undefined,
  longDirection: undefined,
});

const initPositionAndWeatherStateReducer = (
  initialData: IReportTabState,
  initMode: InitMode,
  vesselSpec: VesselSpec | undefined
) => {
  const data = initialData as PositionAndWeatherState;
  switch (initMode) {
    case INIT_MODES.CARRY_OVER:
      return initWithCarryOver(data, vesselSpec);
    case INIT_MODES.IMPORT:
      return initWithExistingReport(data, vesselSpec);
    case INIT_MODES.BLANK:
      return initBlank(vesselSpec);
    default:
      return initBlank(vesselSpec);
  }
};

const getSwellOption = (code: number | undefined) => {
  return SWELL_OPTIONS.find((option) => option.code === code);
};

const createPropellerPitches = (
  numOfPropellers: number | undefined,
  savedPitches?: PropellerPitchState[],
  isImport = false
) => {
  // Reopening a saved report must preserve every previously saved pitch, even if the
  // vessel's propeller count has since changed; a new report always matches the
  // vessel's current propeller count.
  const length = isImport ? Math.max(numOfPropellers ?? 1, savedPitches?.length ?? 0) : (numOfPropellers ?? 1);

  return Array.from({ length }, (_, index) => ({
    pitch: initStateField<number>(savedPitches?.[index]?.pitch ?? 0),
  }));
};

const isControllablePropeller = (vesselSpec?: VesselSpec) =>
  vesselSpec?.propeller?.propeller_type === PropellerTypes.CONTROLLABLE;

const initWithData = (data: PositionAndWeatherState, vesselSpec?: VesselSpec, isImport = false) => {
  return {
    formState: {
      observation: {
        observationTime: initStateField<Date | undefined>(data.observation.observationTime),
        observationTimezone: initStateField<SelectOption>(
          tvaTimezoneToSelectOption(data.observation.observationTimezone)
        ),
      },
      wind: {
        apparentWindSpeed: initStateField<number | undefined>(data.wind.apparentWindSpeed),
        apparentWindDirection: initStateField<number | undefined>(data.wind.apparentWindDirection),
        trueWindReporting: initStateField<boolean | undefined>(data.wind.trueWindReporting),
        trueWindSpeed: initStateField<number | undefined>(data.wind.trueWindSpeed),
        trueWindDirection: initStateField<number | undefined>(data.wind.trueWindDirection),
        trueWindForce: initStateField<number | undefined>(data.wind.trueWindForce),
        trueWindDirectionTrueNorth: initStateField<number | undefined>(data.wind.trueWindDirectionTrueNorth),
      },
      speedAndDistance: {
        tripCounterGps: initStateField<number | undefined>(data.speedAndDistance.tripCounterGps),
        tripCounterLog: initStateField<number | undefined>(data.speedAndDistance.tripCounterLog),
        speedLogMalfunction: initStateField<boolean | undefined>(data.speedAndDistance.speedLogMalfunction),
        logSpeed: initStateField<number | undefined>(data.speedAndDistance.logSpeed),
        logDistance: initStateField<number | undefined>(data.speedAndDistance.logDistance),
        courseOverGround: initStateField<number | undefined>(data.speedAndDistance.courseOverGround),
        observedSpeed: initStateField<number | undefined>(data.speedAndDistance.observedSpeed),
        observedDistance: initStateField<number | undefined>(data.speedAndDistance.observedDistance),
        observedDistanceInEca: initStateField<number | undefined>(data.speedAndDistance.observedDistanceInEca),
        observedDistanceTotalSeaPassage: initStateField<number | undefined>(
          data.speedAndDistance.observedDistanceTotalSeaPassage
        ),
        remainingDistance: initStateField<number | undefined>(data.speedAndDistance.remainingDistance),
        badWeatherHours: initStateField<number | undefined>(data.speedAndDistance.badWeatherHours),
        badWeatherObservedDistance: initStateField<number | undefined>(
          data.speedAndDistance.badWeatherObservedDistance
        ),
        propellerRpm: initStateField<number | undefined>(data.speedAndDistance.propellerRpm),
        propellerPitchPercent: initStateField<number | undefined>(data.speedAndDistance.propellerPitchPercent),
      },
      shipPosition: {
        position: initStateField<LatLngModel | undefined>(
          data.shipPosition.position
            ? {
                latDegrees: data.shipPosition.position.latDegrees,
                latMinutes: data.shipPosition.position.latMinutes,
                latSeconds: data.shipPosition.position.latSeconds,
                latDirection: data.shipPosition.position.latDirection,
                longDegrees: data.shipPosition.position.longDegrees,
                longMinutes: data.shipPosition.position.longMinutes,
                longSeconds: data.shipPosition.position.longSeconds,
                longDirection: data.shipPosition.position.longDirection,
              }
            : createEmptyLatLng()
        ),
      },
      currentsAndSeaWater: {
        seaSwell: initStateField<SelectOption | undefined>(getSwellOption(data.currentsAndSeaWater.seaSwell)),
        seaSwellDirection: initStateField<number | undefined>(data.currentsAndSeaWater.seaSwellDirection),
        waveHeight: initStateField<number | undefined>(data.currentsAndSeaWater.waveHeight),
        waterDepth: initStateField<number | undefined>(data.currentsAndSeaWater.waterDepth),
        currentDirectionTrueNorth: initStateField<number | undefined>(
          data.currentsAndSeaWater.currentDirectionTrueNorth
        ),
        trueCurrentSpeed: initStateField<number | undefined>(data.currentsAndSeaWater.trueCurrentSpeed),
        apparentCurrentDirection: initStateField<number | undefined>(data.currentsAndSeaWater.apparentCurrentDirection),
        apparentCurrentSpeed: initStateField<number | undefined>(data.currentsAndSeaWater.apparentCurrentSpeed),
        apparentWaveDirection: initStateField<number | undefined>(data.currentsAndSeaWater.apparentWaveDirection),
        seaTemperature: initStateField<number | undefined>(data.currentsAndSeaWater.seaTemperature),
        seaWaterDensity: initStateField<number | undefined>(data.currentsAndSeaWater.seaWaterDensity),
        atmosphericPressure: initStateField<number | undefined>(data.currentsAndSeaWater.atmosphericPressure),
        salinity: initStateField<number | undefined>(data.currentsAndSeaWater.salinity),
      },
      ...(isControllablePropeller(vesselSpec) && {
        propellers: {
          propellerPitches: createPropellerPitches(
            vesselSpec?.propeller?.num_of_propellers,
            data.propellers?.propellerPitches,
            isImport
          ),
        },
      }),
    },
  };
};

const initWithExistingReport = (data: PositionAndWeatherState, vesselSpec?: VesselSpec) => {
  return initWithData(data, vesselSpec, true);
};

const initWithCarryOver = (data: PositionAndWeatherState, vesselSpec?: VesselSpec) => {
  return initWithData(data, vesselSpec);
};

const initBlank = (vesselSpec?: VesselSpec) => {
  return {
    formState: {
      observation: {
        observationTime: initStateField<Date>(),
        observationTimezone: initStateField<SelectOption>(),
      },
      wind: {
        apparentWindSpeed: initStateField<number>(),
        apparentWindDirection: initStateField<number>(),
        trueWindReporting: initStateField<boolean>(),
        trueWindSpeed: initStateField<number>(),
        trueWindDirection: initStateField<number>(),
        trueWindForce: initStateField<number>(),
        trueWindDirectionTrueNorth: initStateField<number>(),
      },
      speedAndDistance: {
        tripCounterGps: initStateField<number>(),
        tripCounterLog: initStateField<number>(),
        speedLogMalfunction: initStateField<boolean>(),
        logSpeed: initStateField<number>(),
        logDistance: initStateField<number>(),
        courseOverGround: initStateField<number>(),
        observedSpeed: initStateField<number>(),
        observedDistance: initStateField<number>(),
        observedDistanceInEca: initStateField<number>(),
        observedDistanceTotalSeaPassage: initStateField<number>(),
        remainingDistance: initStateField<number>(),
        badWeatherHours: initStateField<number>(),
        badWeatherObservedDistance: initStateField<number>(),
        propellerRpm: initStateField<number>(),
        propellerPitchPercent: initStateField<number>(),
      },
      shipPosition: {
        position: initStateField<LatLngModel>(),
      },
      currentsAndSeaWater: {
        seaSwell: initStateField<SelectOption>(),
        seaSwellDirection: initStateField<number>(),
        waveHeight: initStateField<number>(),
        waterDepth: initStateField<number>(),
        currentDirectionTrueNorth: initStateField<number>(),
        trueCurrentSpeed: initStateField<number>(),
        apparentCurrentDirection: initStateField<number>(),
        apparentCurrentSpeed: initStateField<number>(),
        apparentWaveDirection: initStateField<number>(),
        seaTemperature: initStateField<number>(),
        seaWaterDensity: initStateField<number>(),
        atmosphericPressure: initStateField<number>(),
        salinity: initStateField<number>(),
      },
      ...(isControllablePropeller(vesselSpec) && {
        propellers: {
          propellerPitches: createPropellerPitches(vesselSpec?.propeller?.num_of_propellers),
        },
      }),
    },
  };
};

export const PositionAndWeatherStore = signalStore(
  { providedIn: 'root' },
  withDevtools('PositionAndWeatherStore'),
  withState(initBlank()),
  withPositionAndWeatherEffects(),
  withFormStoreReducer(positionAndWeatherEvents, initPositionAndWeatherStateReducer, 'positionAndWeather'),
  withComputed((store) => ({
    vm: computed(() => buildPositionAndWeatherVm(store.formState())),
  })),
  // Task #142749 adds the Calculated Values of this tab.
  withCalculatedValues(positionAndWeatherEvents, () => [])
);
